import type { RevenueActualStatus, RevenueActualValue } from '@cortex/domain-ac-mens-hub-api';

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 予実管理の列定義型。
 * @graph-connects none
 */
export interface TargetColumn {
  key: string;
  label: string;
  group: string;
  tab: number;
}

/**
 * 粒度の種類。
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 予実データの表示粒度。
 * @graph-connects none
 */
export type Granularity = 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 予実データ1行分の型。全粒度共通。CPA/割合指標は分母ゼロ時にnullになりうる。
 * @graph-connects none
 */
export interface TargetRow {
  label: string;
  revenue: number;
  subStart: number;
  subAcquired: number;
  /** 社内向けクーポン利用者を除いたサブスク獲得数。CPA 系指標の分母。 */
  subAcquiredExcludingInternalCoupon: number;
  subChurned: number;
  subEnd: number;
  activeStart: number;
  activeAcquired: number;
  activeChurned: number;
  activeEnd: number;
  inactiveChurned: number;
  /** 合計CPA（オファー抜き）＝ 広告費 ÷ 社内利用を除いた獲得数。 */
  cpaTotalVal: number | null;
  adCostTotal: number;
  cpaOps: number | null;
  adCostOps: number;
  adCostOther: number;
  /** 合計CPA（オファーあり）＝（広告費 ＋ オファー）÷ 社内利用を除いた獲得数。実績だけの指標で、計画側は null。 */
  cpaTotalWithOffer: number | null;
  channelRatioDirect: number | null;
  channelRatioReferral: number | null;
  channelRatioAlliance: number | null;
  channelRatioDigital: number | null;
  acqDirect: number;
  acqReferral: number;
  acqAlliance: number;
  acqDigital: number;
  cpaDirect: number | null;
  cpaAlliance: number | null;
  cpaReferral: number | null;
  cpaDigital: number | null;
  costDirect: number;
  costAlliance: number;
  costReferral: number;
  costDigital: number;
  costOpsTotal: number;
  costOther: number;
  cpaOpsOnly: number | null;
  firstMonthRate: number | null;
  onboardRate: number | null;
  firstRating: number;
  inquiryRate: number | null;
  resolveTime: number;
  inquiryCount: number;
}

/**
 * 実績の1行。売上実績は台帳を読み出せなかった期間だけ null（取得失敗。間違った数字を出さない）。
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 予実の実績1行。目標行と同じ形で、売上実績だけは取得失敗のとき null になる。
 * @graph-connects none
 */
export type ActualRow = Omit<TargetRow, 'revenue'> & { revenue: RevenueActualValue };

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business GET /budget-actual/targets のレスポンス型。売上実績（v0.1）の未計算の最初の日と取得失敗（revenueUncomputedFrom・revenueFailure）は API と共通の contract の型から取る。
 * @graph-connects RevenueActualStatus [references] 売上実績の状態
 */
export interface TargetsResponse extends RevenueActualStatus {
  from: string;
  to: string;
  granularity: Granularity;
  lastSyncedAt: string | null;
  columns: TargetColumn[];
  rows: TargetRow[];
  actuals: ActualRow[];
  availableActualMetrics: string[];
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business POST /budget-actual/sync のレスポンス型。獲得実績の成否と、オファー内訳の状態（success / failed / not_started=まだ始まっていない / stale=更新が止まっている）・最終計算日時（日本時間）を区別可能。
 * @graph-connects none
 */
export interface SyncResponse {
  monthlyCount: number;
  dailyCount: number;
  acquisitionCount: number;
  acquisitionStatus: 'success' | 'failed';
  offerCount: number;
  offerStatus: 'success' | 'failed' | 'not_started' | 'stale';
  /** オファー内訳タブの最終計算日時（日本時間 'YYYY-MM-DD HH:mm:ss'）。読めなかったときは null。 */
  offerLastComputedAt: string | null;
  syncedAt: string;
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business オファーの内訳のクーポン1行分の型。割引内容・数えた月数は重複なし・昇順、share は期間の合計オファー額に対する割合（0〜1）。
 * @graph-connects none
 */
export interface OfferBreakdownRow {
  couponName: string;
  discountLabels: string[];
  months: number[];
  count: number;
  offerAmount: number;
  share: number;
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business GET /budget-actual/offer-breakdown のレスポンス型。期間・日次バッチが計算済みの最終日（取り込み前は null）・合計オファー額（税抜）・合計件数と、クーポン名ごとの行（オファー額の降順）。
 * @graph-connects none
 */
export interface OfferBreakdownResponse {
  from: string;
  to: string;
  /** 計算済みの最終日（YYYY-MM-DD）。取り込み前は null。期間の終わりがこれより後なら、その後のオファーは入っていない。 */
  coveredThrough: string | null;
  totalOfferAmount: number;
  totalCount: number;
  rows: OfferBreakdownRow[];
  /** 合計CPA（オファーあり）に含めない割引。上の合計・割合には入らない。 */
  excluded: OfferExcludedSummary;
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 合計CPA（オファーあり）に含めない割引（株主優待・福利厚生）の1種類分。クーポン名・含めない理由・件数・金額（税抜）。
 * @graph-connects none
 */
export interface OfferExcludedRow {
  couponName: string;
  reason: string;
  count: number;
  offerAmount: number;
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 合計CPA（オファーあり）に含めない割引の内訳。一覧の全種類（使われていなければ 0）と合計。
 * @graph-connects none
 */
export interface OfferExcludedSummary {
  totalOfferAmount: number;
  totalCount: number;
  rows: OfferExcludedRow[];
}

/**
 * 粒度ラベルのマッピング。
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 粒度選択UIで使う日本語ラベル。
 * @graph-connects none
 */
export const GRANULARITY_LABELS: Record<Granularity, string> = {
  daily: '日次',
  weekly: '週次',
  monthly: '月次',
  quarterly: '四半期',
  yearly: '年次',
};

/**
 * タブ定義。
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 予実管理の4タブ構成。
 * @graph-connects none
 */
export const BUDGET_TABS = [
  { id: 1, label: '売上・会員数' },
  { id: 2, label: '広告・CPA' },
  { id: 3, label: '獲得チャネル' },
  { id: 4, label: 'CS・品質' },
] as const;
